import { useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionActionsPageData, usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import { createSupporterComment } from '#src/app/pages/actions/shared/api';
import {
	useDisableVideoCreation,
	useSupporterVoicesVideoFirstTrackingProps,
	useVideoFirstFlow,
} from '#src/app/pages/actions/shared/hooks';
import type { SupporterVoice } from '#src/app/pages/actions/shared/types';

type Result = ModelHookResult<
	{
		voiceValue: string;
		addVoiceInProgress: boolean;
	},
	{
		setVoiceValue: (value: string) => void;
		shareVoice: (text: string) => Promise<unknown>;
	}
>;
export function useShareSupporterVoice(): Result {
	const utilityContext = useUtilityContext();
	const [voiceValue, setVoiceValue] = useState<string>('');
	const [addVoiceInProgress, setAddVoiceInProgress] = useState<boolean>(false);
	const track = useTracking();
	const navigate = useNavigate();
	const [
		,
		{
			addSupporterVoice,
			setShareSupporterVoiceError,
			setShareSupporterVoiceSuccess,
			setShouldDisplaySupporterVoicesVideo,
			setNewSupporterVoiceId,
		},
	] = usePetitionActionsPageState();
	const {
		actions: { markCommented },
	} = useVideoFirstFlow();
	const {
		data: {
			petition: { slug, id: petitionId },
		},
	} = usePetitionActionsPageData();
	const disableVideoCreation = useDisableVideoCreation();
	const { translate } = useI18n();
	const trackingProps = useSupporterVoicesVideoFirstTrackingProps();

	const shareVoice = async (text: string) => {
		void track('petition_actions_supporter_voice_share', trackingProps);
		try {
			setAddVoiceInProgress(true);
			const newSupporterVoice: SupporterVoice = await createSupporterComment(petitionId, text, utilityContext);
			if (!disableVideoCreation) {
				setVoiceValue('');
				addSupporterVoice(newSupporterVoice);
				setShareSupporterVoiceSuccess(true);
				// Survives the camera opening, unlike `shareSupporterVoiceSuccess`, so the confirmation can
				// tell that this supporter has nothing left to give.
				markCommented();
				setShouldDisplaySupporterVoicesVideo(true);
				setNewSupporterVoiceId(newSupporterVoice.id);

				void track('petition_actions_supporter_voice_share_success', { text, ...trackingProps });
			} else {
				navigate(`/p/${slug}/d`);
			}
		} catch {
			setShareSupporterVoiceError(translate('corgi.petition_actions.supporter_voice_text.errors.creation'));
		} finally {
			setAddVoiceInProgress(false);
		}
	};

	return {
		data: { voiceValue, addVoiceInProgress },
		actions: {
			setVoiceValue,
			shareVoice,
		},
	};
}
